export type Session = "afternoon" | "evening";
export const EVENT_ID = "idol-island-2026-08-16";

/** How the ticket currently operates on-site. Empty means original presale type. */
export type TicketOpsStatus =
  | ""
  | "onsite_afternoon"
  | "onsite_evening"
  | "onsite_both"
  | "refund";

export type TicketKind = "presale" | "onsite" | "pr" | "refund";
export type TicketSource = "form" | "field";

/** 7/11 credit settle against 8/16 list price. */
export type SettleStatus =
  | "clear"
  | "await_topup"
  | "await_refund"
  | "topup_paid"
  | "refunded";

export interface CheckInState {
  count: number;
  updatedAt?: string;
  device?: string;
}

export interface Ticket {
  id: string;
  name: string;
  phone: string;
  email: string;
  ticketType: string;
  /** Original form ticket type before on-site ops override. */
  originalTicketType?: string;
  quantity: number;
  sessions: Session[];
  afternoonPerformer: string;
  eveningPerformer: string;
  paymentMethod: string;
  transferSuffix: string;
  paymentRecorded: boolean;
  paymentAmount?: string;
  paymentProof?: boolean;
  units: TicketUnit[];
  checkIns: Record<Session, CheckInState>;
  source?: TicketSource;
  kind?: TicketKind;
  opsStatus?: TicketOpsStatus;
  /** NT$ carried from 7/11 keep-credit. */
  priorCredit?: number;
  listPrice?: number;
  settleDelta?: number;
  settleStatus?: SettleStatus | "";
  /**
   * True when staff recorded 現場補款／退款 via checkin-card settle buttons.
   * Bank reconcile `topup_paid` stays false so 錢箱 income excludes 匯款確認補款.
   */
  settlePaidAtDoor?: boolean;
  couponEligible?: boolean;
  couponIssued?: boolean;
  creditSourceId?: string;
}

/** 7/11 keep-credit not yet linked to an 8/16 form ticket. */
export interface DeferredCredit {
  id: string;
  name: string;
  phone: string;
  email: string;
  gmail?: string;
  priorCredit: number;
  couponEligible: boolean;
  couponIssued: boolean;
  redeemed: boolean;
  redeemedTicketId?: string;
  creditNeedsReview?: boolean;
  sourceTicketType?: string;
  transferSuffix?: string;
}

export interface TicketUnit {
  id: string;
  seat: number;
  session: Session;
  performer: string;
  checkedIn: boolean;
  updatedAt?: string;
  device?: string;
}

export interface CheckInMutation {
  kind: "checkin";
  mutationId: string;
  eventId: string;
  ticketId: string;
  unitId: string;
  session: Session;
  delta: 1 | -1;
  expectedCount?: number;
  deviceId: string;
  createdAt: string;
}

export interface PerformerMutation {
  kind: "assignment";
  mutationId: string;
  eventId: string;
  ticketId: string;
  unitId: string;
  session: Session;
  performer: string;
  expectedPerformer: string;
  deviceId: string;
  createdAt: string;
}

export type SyncMutation = CheckInMutation | PerformerMutation;

export interface SyncConflict {
  id: string;
  mutation: SyncMutation;
  message: string;
  actualCount?: number;
  ticket?: Ticket;
  detectedAt: string;
}

export interface ApiConfig {
  endpoint: string;
  token: string;
}

export interface TicketResponse {
  ok: boolean;
  event?: string;
  tickets: Ticket[];
  performerOptions: Record<Session, string[]>;
  generatedAt: string;
  error?: string;
}

export interface MutationResponse {
  ok: boolean;
  ticket?: Ticket;
  duplicate?: boolean;
  code?: string;
  conflict?: {
    expectedCount?: number;
    actualCount?: number;
  };
  error?: string;
}

export interface CreateFieldTicketPayload {
  kind: "onsite" | "pr";
  package: "afternoon" | "evening" | "both";
  deviceId: string;
  name?: string;
  phone?: string;
  email?: string;
  priorCredit?: number;
  listPrice?: number;
  settleDelta?: number;
  settleStatus?: SettleStatus | "";
  couponEligible?: boolean;
  creditSourceId?: string;
  paymentMethod?: string;
}

export interface UpdateTicketOpsPayload {
  ticketId: string;
  opsStatus: TicketOpsStatus;
  deviceId: string;
}

export interface FeePayoutRecord {
  session: Session;
  performer: string;
  amountPaid: number;
  updatedAt?: string;
  device?: string;
}

export interface FeePayoutResponse {
  ok: boolean;
  payouts: FeePayoutRecord[];
  error?: string;
}

export type CashCheckpoint = "opening" | "afternoon" | "evening";

export interface CashCountRecord {
  checkpoint: CashCheckpoint;
  counts: Record<string, number>;
  balance: number;
  income: number;
  feeSnapshot: number;
  feeLocked: boolean;
  updatedAt?: string;
  device?: string;
}

export interface CashCountResponse {
  ok: boolean;
  counts: CashCountRecord[];
  error?: string;
}
